import React, { ReactNode } from "react"
import { useNavigate } from "react-router-dom"
import { ArrowLeft, FileText } from "lucide-react"
import { useCommunicationContext } from "./CommunicationContext"
import Breadcrumb from "./Breadcrumb"
import { Button } from "@/ui/Button"
import { reportShowPath } from "@/QuarterlyReportApp"

interface QuarterlyReportLayoutProps {
  children: ReactNode
  title: string | ReactNode
  subtitle?: string
  actions?: ReactNode
  breadcrumbProps?: {
    householdName?: string
    householdPath?: string
    sectionName?: string
  }
  showBreadcrumb?: boolean
  // Editor-specific props for PDF view toggle
  showEditorActions?: boolean
  reportId?: string
  showPdfView?: boolean
  pdfLoading?: boolean
  pdfUrl?: string | null
  onTogglePdfView?: () => void
}

const QuarterlyReportLayout: React.FC<QuarterlyReportLayoutProps> = ({
  children,
  title,
  subtitle,
  actions,
  breadcrumbProps,
  showBreadcrumb = true,
  showEditorActions = false,
  reportId,
  showPdfView = false,
  pdfLoading = false,
  pdfUrl = null,
  onTogglePdfView,
}) => {
  const { communicationId, communication, isLoading, error } =
    useCommunicationContext()
  const navigate = useNavigate()

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p>Loading...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Failed to load communication: {error.message}
      </div>
    )
  }

  return (
    <div className="quarterly-reports">
      <div className="quarterly-reports-header">
        <div>
          <h1>{title}</h1>
          {subtitle && <p>{subtitle}</p>}
        </div>
        <div className="actions-group">
          {showEditorActions && reportId && onTogglePdfView ? (
            <>
              <Button
                size="md"
                variant={showPdfView ? "filled" : "outline"}
                icon={<FileText size={18} />}
                onClick={onTogglePdfView}
                disabled={pdfLoading}
              >
                {pdfLoading
                  ? "Loading..."
                  : showPdfView
                  ? "Edit View"
                  : "PDF View"}
              </Button>
              <Button
                size="md"
                variant="outline"
                icon={<ArrowLeft size={18} />}
                onClick={() =>
                  navigate(reportShowPath({ communicationId, reportId }))
                }
              >
                Back to Report
              </Button>
            </>
          ) : (
            actions
          )}
        </div>
      </div>

      {showBreadcrumb && (
        <Breadcrumb
          communicationId={communicationId}
          quarter={communication?.quarter}
          year={communication?.year}
          {...breadcrumbProps}
        />
      )}

      {showEditorActions && pdfLoading ? (
        <div className="pdf-loading-container">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading PDF...</span>
            </div>
            <p className="mt-3">Generating PDF preview...</p>
          </div>
        </div>
      ) : showEditorActions && showPdfView && pdfUrl ? (
        <div className="pdf-viewer-container">
          <iframe src={pdfUrl} title="PDF Preview" />
        </div>
      ) : (
        children
      )}
    </div>
  )
}

export default QuarterlyReportLayout
